/**
 * Microphone level maths for the audio settings test.
 *
 * The old meter showed the average of the frequency bins as a percentage, so
 * normal speech sat around 10–30 % and a whisper looked much the same — the
 * agent could not tell "they will hear me" from "they will barely hear me".
 * Here the level is measured as RMS in dBFS, mapped onto the meter, and the
 * meter is split into three bands the UI colours and puts into words:
 *
 *   0 %          silent  (red)     — nothing gets through
 *   up to 40 %   low     (orange)  — heard, but quietly
 *   above 40 %   good    (green)
 *
 * The test captures through acquireMicStream, i.e. with the same constraints
 * as a call (auto gain control on), so the verdict describes what the other
 * side will actually receive.
 */

// The empty end of the meter. A dead or hardware-muted device delivers exact
// zeros (-Infinity dBFS); a live mic in a quiet room with noise suppression
// sits under it too.
export const MIC_METER_FLOOR_DB = -60;
// The top of the "low" band (≈ -36 dBFS). With auto gain control, normal
// speech into a headset peaks around -25…-15 dBFS, i.e. 60–75 %.
export const MIC_LOW_MAX_PERCENT = 40;

// How long the loudest recent level counts, so the verdict doesn't flicker
// to "silent" in the pause between two words.
export const MIC_PEAK_WINDOW_MS = 4000;
// Grace period before judging: the agent needs a moment to start talking and
// auto gain control a moment to converge.
export const MIC_WARMUP_MS = 3000;

export const MIC_VERDICTS = {
  LISTENING: 'listening',
  SILENT: 'silent',
  LOW: 'low',
  GOOD: 'good',
};

export function rmsDbfs(samples) {
  if (!samples?.length) return -Infinity;
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i += 1) {
    sumSquares += samples[i] * samples[i];
  }
  const rms = Math.sqrt(sumSquares / samples.length);
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

// Maps MIC_METER_FLOOR_DB…0 dBFS onto 0…100 so a quiet mic *looks* low.
export function dbToPercent(db) {
  if (!Number.isFinite(db)) return 0;
  const percent = ((db - MIC_METER_FLOOR_DB) / -MIC_METER_FLOOR_DB) * 100;
  return Math.round(Math.min(100, Math.max(0, percent)));
}

export function classifyMicLevel(percent) {
  // Written as "not above" so NaN lands on silent too.
  if (!(percent > 0)) return MIC_VERDICTS.SILENT;
  if (percent <= MIC_LOW_MAX_PERCENT) return MIC_VERDICTS.LOW;
  return MIC_VERDICTS.GOOD;
}

/**
 * Follows a running test frame by frame. `band` is the band of the level right
 * now (what the bar is coloured by); `verdict` judges the loudest level of the
 * last MIC_PEAK_WINDOW_MS so the sentence under the meter stays steady, and it
 * stays on "listening" through the warm-up unless the mic already sounds good.
 */
export function createMicLevelTracker(startedAt) {
  let samples = [];

  return {
    push(db, now) {
      const percent = dbToPercent(db);
      samples.push({ percent, at: now });
      samples = samples.filter(s => now - s.at <= MIC_PEAK_WINDOW_MS);
      const peakVerdict = classifyMicLevel(
        Math.max(...samples.map(s => s.percent))
      );

      const warmingUp = now - startedAt < MIC_WARMUP_MS;
      return {
        percent,
        band: classifyMicLevel(percent),
        verdict:
          warmingUp && peakVerdict !== MIC_VERDICTS.GOOD
            ? MIC_VERDICTS.LISTENING
            : peakVerdict,
      };
    },
  };
}
